import React, { useState, useEffect } from "react";
import { 
  addStaffLeaveService,
  getStaffLeavesService,
  updateStaffLeaveService,
  deleteStaffLeaveService,
  getAllStaffService,
  // Make sure these student endpoints are exported in your api-service.js
  addStudentLeaveService,
  getStudentLeavesService,
  updateStudentLeaveService,
  deleteStudentLeaveService,
  get_all_registered_students
} from "../../services/api-service"; 

const LeaveManagement = () => {
  // --- States ---
  const [userType, setUserType] = useState("staff"); // 'staff' or 'student'
  
  const [userList, setUserList] = useState([]);
  const [leaves, setLeaves] = useState([]);
  
  const [editMode, setEditMode] = useState(false);
  const [currentLeaveId, setCurrentLeaveId] = useState(null);
  
  const [formData, setFormData] = useState({
    user_id: "",
    user_name: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  
  const [status, setStatus] = useState({ loading: false, message: "", type: "" });

  // --- Data Fetching ---
  const fetchUsers = async () => {
    try {
      if (userType === "staff") {
        const staffRes = await getAllStaffService();
        const formattedStaff = (staffRes.data || staffRes || []).map(s => ({
          id: s.staff_id,
          name: `${s.first_name || ""} ${s.last_name || ""}`.trim()
        }));
        setUserList(formattedStaff);
      } else {
        const studentRes = await get_all_registered_students();
        const formattedStudents = (studentRes.data || studentRes || []).map(s => ({
          id: s.student_id,
          name: `${s.first_name || ""} ${s.last_name || ""}`.trim()
        }));
        setUserList(formattedStudents);
      }
    } catch (error) {
      console.error(`Failed to load ${userType}:`, error);
      setUserList([]);
    }
  };

  const fetchLeaves = async () => {
    try {
      if (userType === "staff") {
        const leaveRes = await getStaffLeavesService();
        const formattedLeaves = (leaveRes.data?.data || []).map(l => ({
          ...l,
          user_id: l.staff_id,
          user_name: l.staff_name
        }));
        setLeaves(formattedLeaves);
      } else {
        const leaveRes = await getStudentLeavesService();
        const formattedLeaves = (leaveRes.data?.data || []).map(l => ({
          ...l,
          user_id: l.student_id,
          user_name: l.student_name
        }));
        setLeaves(formattedLeaves);
      }
    } catch (error) {
      console.error(`Failed to load ${userType} leaves:`, error);
      setLeaves([]);
    }
  };

  useEffect(() => {
    cancelEdit();
    fetchUsers();
    fetchLeaves();
  }, [userType]);

  // --- Handlers ---
  const handleUserTypeToggle = (type) => {
    if (userType !== type) {
      setUserType(type);
      setStatus({ loading: false, message: "", type: "" });
    }
  };

  const handleUserChange = (e) => {
    const selectedId = e.target.value;
    const selectedUser = userList.find((u) => u.id === selectedId);
    
    setFormData({
      ...formData,
      user_id: selectedId,
      user_name: selectedUser ? selectedUser.name : "",
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, message: "", type: "" });

    // Format payload based on userType to match backend expectations
    const payload = userType === "staff" 
      ? {
          staff_id: formData.user_id,
          staff_name: formData.user_name,
          start_date: formData.start_date,
          end_date: formData.end_date,
          reason: formData.reason,
        }
      : {
          student_id: formData.user_id,
          student_name: formData.user_name,
          start_date: formData.start_date,
          end_date: formData.end_date,
          reason: formData.reason,
        };

    try {
      if (editMode) {
        if (userType === "staff") {
          await updateStaffLeaveService(currentLeaveId, payload);
        } else {
          await updateStudentLeaveService(currentLeaveId, payload);
        }
        setStatus({ loading: false, message: "Leave updated successfully.", type: "success" });
      } else {
        if (userType === "staff") {
          await addStaffLeaveService(payload);
        } else {
          await addStudentLeaveService(payload);
        }
        setStatus({ loading: false, message: "Leave added successfully.", type: "success" });
      }

      cancelEdit();
      fetchLeaves(); 
      
    } catch (error) {
      setStatus({ loading: false, message: "Failed to save leave request.", type: "danger" });
    }
  };

  const handleEdit = (leave) => {
    setEditMode(true);
    setCurrentLeaveId(leave.id);
    setFormData({
      user_id: leave.user_id,
      user_name: leave.user_name,
      start_date: leave.start_date,
      end_date: leave.end_date,
      reason: leave.reason
    });
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm("Are you sure you want to delete this leave record?")) return;

    try {
      if (userType === "staff") {
        await deleteStaffLeaveService(leaveId);
      } else {
        await deleteStudentLeaveService(leaveId);
      }
      setStatus({ loading: false, message: "Leave deleted successfully.", type: "warning" });
      fetchLeaves(); 
    } catch (error) {
      setStatus({ loading: false, message: "Failed to delete leave.", type: "danger" });
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setCurrentLeaveId(null);
    setFormData({ user_id: "", user_name: "", start_date: "", end_date: "", reason: "" });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="content">
      <div className="container-fluid py-4">
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-bold mb-0 text-dark">Leave Management</h3>
            <p className="text-muted mb-0">Track and manage staff and Member absences</p>
          </div>
          
          {/* Toggle Switch */}
          <div className="btn-group bg-white p-1 rounded-pill shadow-sm border" role="group">
            <button 
              type="button" 
              className={`btn rounded-pill px-4 fw-bold ${userType === 'staff' ? 'btn-primary' : 'btn-light text-muted border-0'}`}
              onClick={() => handleUserTypeToggle('staff')}
            >
              Staff Leave
            </button>
            <button 
              type="button" 
              className={`btn rounded-pill px-4 fw-bold ${userType === 'student' ? 'btn-primary' : 'btn-light text-muted border-0'}`}
              onClick={() => handleUserTypeToggle('student')}
            >
              Member Leave
            </button>
          </div>
        </div>

        {status.message && (
          <div className={`alert alert-${status.type} alert-dismissible fade show shadow-sm border-0 rounded-3`} role="alert">
            <i className={`bi ${status.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2`}></i>
            {status.message}
            <button type="button" className="btn-close" onClick={() => setStatus({ ...status, message: "" })}></button>
          </div>
        )}

        <div className="row g-4">
          {/* Form Column */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-transparent border-0 pt-4 pb-2 px-4">
                <h5 className="card-title fw-bold text-dark mb-0">
                  <i className={`bi ${editMode ? "bi-pencil-square text-warning" : "bi-calendar-plus text-primary"} me-2`}></i>
                  {editMode ? `Edit ${userType === 'staff' ? 'Staff' : 'Student'} Leave` : `New ${userType === 'staff' ? 'Staff' : 'Member'} Leave`}
                </h5>
              </div>
              <div className="card-body px-4 pb-4">
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label className="form-label fw-semibold text-secondary small text-uppercase">
                      {userType === 'staff' ? 'Staff Member' : 'Member'}
                    </label>
                    <select className="form-select bg-light border-0" name="user_id" value={formData.user_id} onChange={handleUserChange} required>
                      <option value="">-- Choose {userType === 'staff' ? 'Staff' : 'Member'} --</option>
                      {userList.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.id})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="row">
                    <div className="col-6 form-group mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase">Start Date</label>
                      <input type="date" className="form-control bg-light border-0" name="start_date" value={formData.start_date} onChange={handleChange} required />
                    </div>
                    <div className="col-6 form-group mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase">End Date</label>
                      <input type="date" className="form-control bg-light border-0" name="end_date" value={formData.end_date} min={formData.start_date} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="form-group mb-4">
                    <label className="form-label fw-semibold text-secondary small text-uppercase">Reason</label>
                    <textarea className="form-control bg-light border-0" name="reason" rows="3" placeholder="Add a brief description..." value={formData.reason} onChange={handleChange} required></textarea>
                  </div>
                  
                  <div className="d-grid gap-2">
                    <button type="submit" className={`btn rounded-pill fw-bold ${editMode ? "btn-warning text-dark" : "btn-primary"}`} disabled={status.loading}>
                      {status.loading ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Saving...</>
                      ) : (
                        editMode ? "Update Record" : "Submit Leave"
                      )}
                    </button>
                    {editMode && (
                      <button type="button" className="btn btn-light rounded-pill fw-bold text-muted" onClick={cancelEdit}>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Table Column */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-header bg-transparent border-0 pt-4 pb-2 px-4 d-flex justify-content-between align-items-center">
                <h5 className="card-title fw-bold text-dark mb-0">
                  <i className="bi bi-card-list text-primary me-2"></i> Recent {userType === 'staff' ? 'Staff' : 'Member'} Leaves
                </h5>
                <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                  Total: {leaves.length}
                </span>
              </div>
              <div className="card-body px-4 pb-4">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light text-secondary">
                      <tr>
                        <th className="border-0 rounded-start">Name</th>
                        <th className="border-0">Duration</th>
                        <th className="border-0">Reason</th>
                        <th className="border-0 rounded-end text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="border-top-0">
                      {leaves.length > 0 ? (
                        leaves.map((leave) => {
                          return (
                          <tr key={leave.id} className="border-bottom">
                            <td className="py-3">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex justify-content-center align-items-center fw-bold me-3" style={{ width: "40px", height: "40px" }}>
                                  {leave.user_name?.charAt(0) || "-"}
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-bold">{leave.user_name}</h6>
                                  <small className="text-muted">{leave.user_id}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              {leave.start_date === leave.end_date ? (
                                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2 py-1 rounded-pill">
                                  {formatDate(leave.start_date)}
                                </span>
                              ) : (
                                <div className="d-flex flex-column gap-1">
                                  <span className="badge bg-light text-dark border px-2 py-1 rounded-pill w-auto text-start">
                                    <i className="bi bi-box-arrow-in-right text-success me-1"></i> {formatDate(leave.start_date)}
                                  </span>
                                  <span className="badge bg-light text-dark border px-2 py-1 rounded-pill w-auto text-start">
                                    <i className="bi bi-box-arrow-left text-danger me-1"></i> {formatDate(leave.end_date)}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              <p className="mb-0 text-truncate" style={{ maxWidth: "200px" }} title={leave.reason}>
                                {leave.reason}
                              </p>
                            </td>
                            <td className="text-end">
                              <button className="btn btn-light btn-sm rounded-circle text-primary me-2 shadow-sm" onClick={() => handleEdit(leave)} title="Edit">
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                              <button className="btn btn-light btn-sm rounded-circle text-danger shadow-sm" onClick={() => handleDelete(leave.id)} title="Delete">
                                <i className="bi bi-trash-fill"></i>
                              </button>
                            </td>
                          </tr>
                        )})
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-calendar-check display-4 d-block mb-3 opacity-50"></i>
                              <h5>No active leaves</h5>
                              <p className="small">There are currently no {userType} leaves recorded.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default LeaveManagement;
